import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { buildDocumentSelectionDisplayRows, getDocumentSelectionPopoverLayout } from '../utils/documentSelection';
import { decodeFileName } from '../utils/fileNameUtils';

export default function DocumentSelectionSummary({ selectedKeys, folderName, onRemove, onClear }) {
    const rows = buildDocumentSelectionDisplayRows(selectedKeys, folderName);
    const elsewhere = rows.filter(row => !row.isCurrentFolder).length;
    const [open, setOpen] = useState(false);
    const [layout, setLayout] = useState(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const position = useCallback(() => {
        if (!triggerRef.current) return;
        setLayout(getDocumentSelectionPopoverLayout({
            triggerRect: triggerRef.current.getBoundingClientRect(),
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        }));
    }, []);
    useEffect(() => {
        if (!open) return undefined;
        position();
        const dismissOutside = event => {
            if (!triggerRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) setOpen(false);
        };
        const dismissEscape = event => {
            if (event.key === 'Escape') {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        window.addEventListener('resize', position);
        window.addEventListener('scroll', position, true);
        document.addEventListener('pointerdown', dismissOutside);
        document.addEventListener('keydown', dismissEscape);
        return () => {
            window.removeEventListener('resize', position);
            window.removeEventListener('scroll', position, true);
            document.removeEventListener('pointerdown', dismissOutside);
            document.removeEventListener('keydown', dismissEscape);
        };
    }, [open, position]);
    const { placement, ...popoverStyle } = layout || {};
    return (
        <div className="document-selection-summary">
            <button type="button" ref={triggerRef} className="action quaternary small-text document-selection-summary-trigger"
                title="Review selected documents" aria-expanded={open}
                onClick={() => setOpen(current => !current)}>
                <span>{rows.length} selected</span>
                {elsewhere > 0 && <span className="subtext document-selection-summary-elsewhere">{elsewhere} elsewhere</span>}
            </button>
            {open && layout && createPortal(<div ref={popoverRef} style={popoverStyle} data-placement={placement}
                className="document-selection-summary-popover" role="group" aria-label="Selected documents">
                <div className="document-selection-summary-header">
                    <div>
                        <strong>Selected documents</strong>
                        <p className="small-text">These remain selected as you move through this case.</p>
                    </div>
                    <button type="button" className="action quaternary small-text" onClick={onClear}>Clear all</button>
                </div>
                <div className="document-selection-summary-list">
                    {rows.map(row => (
                        <div className="document-selection-summary-row" key={row.key}>
                            <div className="document-selection-summary-copy">
                                <span>{decodeFileName(row.name)}</span>
                                <span className="small-text">{decodeFileName(row.parentPath || 'Case documents')}</span>
                            </div>
                            <button type="button" className="action quaternary"
                                aria-label={`Remove ${decodeFileName(row.name)} from selection`}
                                onClick={() => onRemove(row.key)}><X size={15} /></button>
                        </div>
                    ))}
                </div>
            </div>, document.body)}
        </div>
    );
}
